
import { Box, Button, Card, CardActions, CardContent, CardHeader, Checkbox, Collapse, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import htmlParse from 'html-react-parser';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActionMenu from "../../components/action-menu/ActionMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellSlash, IconDefinition } from "@elynx/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { SetpointUsageCard } from "./SetpointUsageCard";
import { SetPointListActionEnum, SetPointListActionItem, Setpoint } from "./AdminSetpointListUtils";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useSetpointUsageData } from "../../queries/alarm-setpoint-admin/UseSetpointUsageData";
import LoadingZone from "../loading-zone/LoadingZone";


export enum SetpointActionEnum {
    Edit = 1,
    Delete = 2,
    ToggleExpansion = 3
}

export type AdminSetpointListItemProps = {
    groupID: number,
    item: Setpoint,
    onAction: (action: SetPointListActionEnum, payload: Setpoint) => void,
    selectable?: boolean
};

export function AdminSetpointListItem({
    groupID,
    item,
    onAction,
    selectable
}: AdminSetpointListItemProps) {

    const isGrouped = item.groupKey !== '';
    const [expanded, setExpanded] = useState(isGrouped);
    const theme = useTheme();
    const { t } = useTranslation();

    const {data: usageData, status } = useQueryNotifications(useSetpointUsageData)({
        groupID,
        setpointID: item.id,
        enabled: !isGrouped && expanded
    });

    const usage = isGrouped ? item.usage : (usageData ?? []);

    const deviceUsage = usageData?.filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Device) ?? [];
    const tankUsage = usageData?.filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Tank) ?? [];
    const tankBatteryUsage = usageData?.filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.TankBattery) ?? [];
    const wellUsage = usageData?.filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Well) ?? [];


    const objectName = item.groupKey;


    const actionItems: SetPointListActionItem[] = useMemo(() => {

        let actions = [{
            label: t("setpointAdmin.actions.edit"),
            action: SetPointListActionEnum.Edit,
            onClick: () => {
                onAction(SetPointListActionEnum.Edit, item);
            },
        }, {
            label: t("setpointAdmin.actions.rename"),
            action: SetPointListActionEnum.Rename,
            onClick: () => {
                onAction(SetPointListActionEnum.Rename, item);
            },
        }, {
            label: t("setpointAdmin.actions.copy"),
            action: SetPointListActionEnum.Copy,
            onClick: () => {
                onAction(SetPointListActionEnum.Copy, item);
            },
        },{
            label: t("setpointAdmin.actions.delete"),
            action: SetPointListActionEnum.Delete,
            onClick: () => {
                onAction(SetPointListActionEnum.Delete, item);
            },
        }];

        if (isGrouped) {
            actions.push({
                label: t("setpointAdmin.disableTest"),
                action: SetPointListActionEnum.DisableTest,
                onClick: () => {
                    onAction(SetPointListActionEnum.DisableTest, item);
                },
            })
        }
        
        
        return actions;

    }, [isGrouped,
        item,
        t,
        onAction
    ]);

    // function toggleExpanded() {
    //     onAction(RoleActionEnum.ToggleExpansion, item);
    // }

    function getTitle(): ReactNode {
        return (
            <Box>
                {`${item.name} (${item.isDisabled ? 'Field Alarm' : 'Database Alarm'})`}
            </Box>
        );
    }

    function getSubheader(): ReactNode {
        return (
            <Box>
                {htmlParse(item.description)}
            </Box>
        );
    }

    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(selectable ? 8 : 4),
                display: 'flex'
            }}
            >
                <>

                {selectable && 
                    <Checkbox color='secondary' 
                        checked={item.selected} 
                        onChange={() => onAction(SetPointListActionEnum.Select, item) }
                    />
                }


                <FontAwesomeIcon icon={getIcon()} size="2x" color={getIconColor()}  
                    style={{
                        marginTop: 'auto',
                        marginBottom: 'auto'
                    }}/>
                </>                
            </Box>
        );
    }

    function getIcon(): IconDefinition {
        return item.isDisabled ? faBellSlash : faBell;

    }

    function getIconColor(): string | undefined {
        return item.isDisabled ?
            theme.palette.grey[600] :
            item.hasTagsInAlarm ?
            item.alarmColor :
            undefined;
    }

    return (
        <Card variant="outlined" sx={{minHeight: '4rem'}}>
            <CardHeader
                sx={{
                    padding: 1,
                    paddingBottom: 0
                }}
                title={getTitle()}
                avatar={getAvatar()}
                subheader={getSubheader()}
                action={!selectable ? <ActionMenu actionItems={actionItems} />  : undefined}
            />
            
            {!isGrouped && item.linkedTagCount > 0 &&
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => { setExpanded(!expanded)}
                        // toggleExpanded
                    }
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    {t('setpointAdmin.usage', {count: item.linkedTagCount})}
                </Button>
            </CardActions>
            }

            <Collapse in={expanded} timeout="auto">
                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4)
                }}>
                    <LoadingZone isLoading={status === "loading"}>
                        {
                        
                            isGrouped ?

                            <SetpointUsageCard 
                                title={t('setpointAdmin.linkedTagText', {name: objectName})}
                                alarmColor={item.alarmColor}
                                acknowledgeColor={item.acknowledgeColor}
                                items={usage}
                                grouped={true}
                                onTestOrDisableAction={() => onAction(SetPointListActionEnum.DisableTest, item)}
                                hideActions={selectable}
                                />
                                : null
                        }

                        {
                            !isGrouped && deviceUsage.length ?

                                <SetpointUsageCard 
                                    title={t('setpointAdmin.objectUsage', {objectType: getObjectTypeName(eLynxObjectTypeEnum.Device), count: deviceUsage.length})}
                                    alarmColor={item.alarmColor}
                                    acknowledgeColor={item.acknowledgeColor}
                                    items={deviceUsage}
                                    grouped={false} 
                                    onTestOrDisableAction={() => onAction(SetPointListActionEnum.DisableTest, item)} 
                                    hideActions={selectable}
                                    />
                                : null
                        }

                        {
                            !isGrouped && tankUsage.length ?

                                <SetpointUsageCard 
                                    title={t('setpointAdmin.objectUsage', {objectType: getObjectTypeName(eLynxObjectTypeEnum.Tank), count: tankUsage.length})}
                                    alarmColor={item.alarmColor}
                                    acknowledgeColor={item.acknowledgeColor}
                                    items={tankUsage}
                                    grouped={false} 
                                    onTestOrDisableAction={() => onAction(SetPointListActionEnum.DisableTest, item)}
                                    hideActions={selectable}
                                    />
                                : null
                        }

                        {
                            !isGrouped && tankBatteryUsage.length ?

                                <SetpointUsageCard 
                                    title={t('setpointAdmin.objectUsage', {objectType: getObjectTypeName(eLynxObjectTypeEnum.TankBattery), count: tankBatteryUsage.length})}
                                    alarmColor={item.alarmColor}
                                    acknowledgeColor={item.acknowledgeColor}
                                    items={tankBatteryUsage}
                                    grouped={false} 
                                    onTestOrDisableAction={() => onAction(SetPointListActionEnum.DisableTest, item)}                                    
                                    hideActions={selectable}
                                    />
                                : null
                        }

                        {
                            !isGrouped && wellUsage.length ?

                                <SetpointUsageCard 
                                    title={t('setpointAdmin.objectUsage', {objectType: getObjectTypeName(eLynxObjectTypeEnum.Well), count: wellUsage.length})}
                                    alarmColor={item.alarmColor}
                                    acknowledgeColor={item.acknowledgeColor}
                                    items={wellUsage}
                                    grouped={false} 
                                    onTestOrDisableAction={() => onAction(SetPointListActionEnum.DisableTest, item)}
                                    hideActions={selectable}
                                    />
                                : null
                        }

                    </LoadingZone>                    
                </CardContent>
            </Collapse>
        </Card>
    );
}
